import React, { useCallback, useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";

const KINDS = { leads: ["客户", "Customers"], projects: ["工程项目", "Construction projects"] };
const SALES_FIELDS = { leads: "assignedSales", projects: "sales" };
const label = (record) => record?.clientName || record?.name || record?.newAddress || record?.address || record?.id || record?.recordId || "—";

// 人工确认候选项；此处不转移业务，也不改变原销售业绩。
export function TransitionBusinessReview({ transition, zh }) {
  const { api } = useApp();
  const T = (cn, en) => (zh ? cn : en);
  const [kind, setKind] = useState("leads");
  const [page, setPage] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [refresh, setRefresh] = useState(0);
  const tid = encodeURIComponent(transition.id);
  const editable = transition.status === "open";

  useEffect(() => {
    let active = true;
    setPage(null); setPrepared(null); setError("");
    Promise.all([
      api.get(`/hrms/transitions/${tid}/business-candidates`, { params: { kind } }),
      api.get(`/hrms/transitions/${tid}/business-prepared`),
    ]).then(([a, b]) => { if (active) { setPage(a.data); setPrepared(b.data); } })
      .catch((err) => { if (active) setError(err?.response?.data?.detail || T("无法读取业务清单", "Could not load the business checklist")); });
    return () => { active = false; };
  }, [api, tid, kind, refresh, zh]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(async () => {
    if (page?.nextOffset == null || busy) return;
    setBusy("more");
    try {
      const { data } = await api.get(`/hrms/transitions/${tid}/business-candidates`, { params: { kind, offset: page.nextOffset } });
      setPage((p) => ({ ...data, items: [...p.items, ...data.items] }));
    } catch (err) { setError(err?.response?.data?.detail || T("加载更多失败", "Could not load more")); }
    finally { setBusy(""); }
  }, [api, tid, kind, page, busy, zh]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (item) => {
    const name = label(item);
    if (!window.confirm(T(`确认「${name}」属于 ${transition.employeeName}？姓名可能重复。确认后只加入核对清单，业务负责人不会改变。`, `Confirm that "${name}" belongs to ${transition.employeeName}? Names can match multiple people. This prepares the checklist without changing the owner.`))) return;
    setBusy(item.id); setError("");
    try {
      await api.post(`/hrms/transitions/${tid}/business-candidates/${kind}/${encodeURIComponent(item.id)}`,
        { sourceValue: item[SALES_FIELDS[kind]], identityConfirmed: true });
      setRefresh((n) => n + 1);
    } catch (err) { setError(err?.response?.data?.detail || T("确认失败，请重新核对", "Could not confirm; review and retry")); }
    finally { setBusy(""); }
  };

  const withdraw = async (item) => {
    if (!window.confirm(T(`从核对清单撤回「${label({ ...item.record, recordId: item.recordId })}」？`, `Remove "${label({ ...item.record, recordId: item.recordId })}" from the prepared checklist?`))) return;
    setBusy(item.recordId); setError("");
    try {
      await api.delete(`/hrms/transitions/${tid}/business-candidates/${item.kind}/${encodeURIComponent(item.recordId)}`);
      setRefresh((n) => n + 1);
    } catch (err) { setError(err?.response?.data?.detail || T("撤回失败，请刷新", "Could not remove; reload")); }
    finally { setBusy(""); }
  };

  const morePrepared = async () => {
    if (prepared?.nextOffset == null || busy) return;
    setBusy("prepared-more");
    try {
      const { data } = await api.get(`/hrms/transitions/${tid}/business-prepared`, { params: { offset: prepared.nextOffset } });
      setPrepared((p) => ({ ...data, items: [...p.items, ...data.items] }));
    } catch (err) { setError(err?.response?.data?.detail || T("加载更多失败", "Could not load more")); }
    finally { setBusy(""); }
  };

  return (
    <section className="space-y-2 rounded-lg border border-border bg-muted/30 p-3 text-xs" data-testid={`hrms-transition-business-${transition.id}`}>
      <h3 className="font-semibold text-foreground">{T("业务交接核对清单", "Business handover checklist")}</h3>
      <p className="text-muted-foreground">{T("候选按原员工姓名查找，可能存在同名；请逐条确认。加入清单不会转移客户、项目或业绩。", "Candidates are matched by the original employee's name and can include namesakes. Confirm each record. Preparing the checklist does not transfer customers, projects, or sales credit.")}</p>
      {error && <p role="alert" className="text-destructive">{error} <button type="button" className="underline" onClick={() => setRefresh((n) => n + 1)}>{T("重新加载", "Reload")}</button></p>}
      {prepared && <details className="rounded-md border border-border bg-background px-2 py-2">
        <summary className="cursor-pointer font-medium text-foreground">{T("已核对，待交接", "Prepared, awaiting handover")} · {prepared.total}</summary>
        {prepared.items.length === 0 && <p className="mt-2 text-muted-foreground">{T("暂无已核对记录", "No records prepared")}</p>}
        <ul className="mt-2 space-y-2">
          {prepared.items.map((item) => <li key={`${item.kind}-${item.recordId}`} className="flex flex-wrap items-center gap-2 text-muted-foreground">
            <span className="font-medium text-foreground">{label({ ...item.record, recordId: item.recordId })}</span><span>{KINDS[item.kind]?.[zh ? 0 : 1]} · {item.recordId}</span>
            {item.sourceChanged && <span className="text-amber-800">{T("原负责人或关联业务已变化，请重新核对", "Original owner or linked records changed; review again")}</span>}
            {item.status === "prepared" ? <span>{T("待经理接手", "Awaiting manager")}</span>
              : item.status === "manager_owned" ? <span>{T("经理已接手", "Manager took over")}</span>
              : item.status === "assigned" && <span>{T("已分配", "Assigned")}: {item.assignedTo?.name || "—"}</span>}
            {editable && item.status === "prepared" && <button type="button" disabled={!!busy} onClick={() => withdraw(item)} className="ml-auto text-brand-600 underline disabled:opacity-50">{T("撤回核对", "Remove")}</button>}
          </li>)}
        </ul>
        {prepared.nextOffset != null && <button type="button" onClick={morePrepared} disabled={!!busy} className="mt-2 text-brand-600 underline">{T("加载更多", "Load more")}</button>}
      </details>}
      <div className="flex flex-wrap gap-2">
        {Object.entries(KINDS).map(([key, names]) => <button key={key} type="button" onClick={() => setKind(key)} aria-pressed={kind === key} className={`rounded-md border px-2 py-1 ${kind === key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground"}`}>{names[zh ? 0 : 1]}</button>)}
      </div>
      {!page && !error && <p role="status" className="text-muted-foreground">{T("正在读取候选记录", "Loading candidates")}</p>}
      {page && <div className="rounded-md border border-border bg-background p-2">
        <p className="text-muted-foreground">{T(`候选 ${page.total} 条`, `${page.total} candidates`)}</p>
        {page.items.length === 0 && <p className="mt-2 text-muted-foreground">{T("未查到候选记录；未匹配到的业务仍需另行核对。", "No candidates found. Other records may still require review.")}</p>}
        <ul className="mt-2 space-y-2">
          {page.items.map((item) => <li key={item.id} className="flex flex-wrap items-center gap-2 text-muted-foreground">
            <span className="font-medium text-foreground">{label(item)}</span><span>{item.address && item.address !== label(item) ? `${item.address} · ` : ""}{item.id}</span>
            <span>{T("原销售", "Original salesperson")}: {item[SALES_FIELDS[kind]]}</span>
            {item.prepared ? <span className="ml-auto">{T("已核对", "Prepared")}</span> : editable && <button type="button" disabled={!!busy} onClick={() => confirm(item)} className="ml-auto rounded-md border border-primary px-2 py-1 text-primary disabled:opacity-50">{T("确认属于该员工", "Confirm record")}</button>}
          </li>)}
        </ul>
        {page.nextOffset != null && <button type="button" onClick={loadMore} disabled={!!busy} className="mt-2 text-brand-600 underline">{T("加载更多", "Load more")}</button>}
      </div>}
    </section>
  );
}
